import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

import { catchError, concat, Observable, tap } from 'rxjs';
import produce from 'immer';
import { SearchUsers, SignInAgent } from './agent.actions';
import { ApiStoreData, defaultApiStoreData, InvalidApiResponseDataError } from '../utils';
import {
  ApiSearchUsersResponse,
  ApiSignInAgentWithOtpResponse,
  mapSearchUsers,
  AgentLoadedPatient,
  SearchUsersResult,
  mapSearchUsersToAgentLoadedPatient,
  UserRole,
} from '../../models';
import { ApiService, SentryService } from '../../services';
import { ClearAuthStateSubscription, ResetState, SetAgentAuthValues, SetAuthenticatedUser } from '../user/user.actions';

export interface AgentStateModel {
  agentPreviewPatientId: number | null;
  searchUsers: ApiStoreData<SearchUsersResult | null>;
  signInAgentWithOtp: ApiStoreData<ApiSignInAgentWithOtpResponse | null>;
  agentLoadedPatient: AgentLoadedPatient | null;
}

const defaults = {
  agentPreviewPatientId: null,
  searchUsers: defaultApiStoreData,
  signInAgentWithOtp: defaultApiStoreData,
  agentLoadedPatient: null,
};

@State<AgentStateModel>({
  name: 'agent',
  defaults,
})
@Injectable({ providedIn: 'root' })
export class AgentState {
  constructor(private store: Store, private apiService: ApiService, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static agentPreviewPatientId(state: AgentStateModel): number | null {
    return state.agentPreviewPatientId;
  }

  @Selector()
  static searchUsersState(state: AgentStateModel): ApiStoreData<ApiSearchUsersResponse | null> {
    return state.searchUsers;
  }

  @Selector()
  static signInAgentWithOtpState(state: AgentStateModel): ApiStoreData<ApiSignInAgentWithOtpResponse | null> {
    return state.signInAgentWithOtp;
  }

  @Selector()
  static agentLoadedPatient(state: AgentStateModel): AgentLoadedPatient | null {
    return state.agentLoadedPatient;
  }

  @Action(SignInAgent)
  signInAgent(ctx: StateContext<AgentStateModel>, action: SignInAgent) {
    ctx.dispatch([new ClearAuthStateSubscription(), new ResetState()]).subscribe(() => {
      ctx.setState(
        produce((draft) => {
          draft.signInAgentWithOtp.isLoading = true;
        })
      );
    });

    return this.apiService.signInAgentWithOtp(action.otp).pipe(
      tap({
        next: (response) => {
          const { patientId, token, username } = response.data;

          const agentAuthenticatedUser = { email: username, oktaId: 'agent-okta-id', role: UserRole.Agent };

          ctx.setState(
            produce((draft) => {
              draft.agentPreviewPatientId = patientId;
            })
          );

          return concat(
            ctx.dispatch([new SetAgentAuthValues(token), new SetAuthenticatedUser(agentAuthenticatedUser)]),
            ctx.dispatch(new SearchUsers({ patientId })),
            this.handleSuccess({ ctx, stateKey: 'signInAgentWithOtp', data: response.data })
          );
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'signInAgentWithOtp' }))
    );
  }

  @Action(SearchUsers)
  searchUsers(ctx: StateContext<AgentStateModel>, action: SearchUsers) {
    ctx.setState(
      produce((draft) => {
        draft.searchUsers.isLoading = true;
      })
    );

    return this.apiService.searchUsers(action.formValues.patientId).pipe(
      tap({
        next: (response) => {
          const data = response.data.map((u) => mapSearchUsers(u));

          if (data.length === 0) {
            const error = new InvalidApiResponseDataError(['No active portal users found for this patient']);
            this.handleError({ ctx, error, stateKey: 'searchUsers', skipRecordingError: true });
          } else if (data.length > 1) {
            const error = new InvalidApiResponseDataError([
              'More than one user is found for this patient. Please contact portal administrator.',
            ]);
            this.handleError({ ctx, error, stateKey: 'searchUsers' });
          } else {
            const selectedPatient = data[0].patients.find((p) => p.patientId === action.formValues.patientId);

            if (!selectedPatient) {
              const error = new InvalidApiResponseDataError([
                'Invalid search result. Please contact portal administrator.',
              ]);
              this.handleError({ ctx, error, stateKey: 'searchUsers' });
            } else {
              ctx.setState(
                produce((draft) => {
                  draft.agentLoadedPatient = mapSearchUsersToAgentLoadedPatient(data[0], selectedPatient);
                })
              );

              this.handleSuccess({ ctx, data, stateKey: 'searchUsers' });
            }
          }
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'searchUsers' }))
    );
  }
}
